import React from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import ReactSelect from 'react-select'
import tw, { theme } from 'twin.macro'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { useConfig } from '../ConfigProvider'
import { useForm } from '../Form/context'
import { useInputGroup } from '../InputGroup/context'
import { HiCheck, HiChevronDown, HiX } from 'react-icons/hi'
import Spinner from '../Spinner'
import Tooltip from '../Tooltip'
import { CONTROL_SIZES, SIZES } from '../utils/constant'

const DEFAULT_PLACEHOLDER = 'Select...'
const SELECT_MENU_Z_INDEX = 9999

const getDisplayText = (value) => {
    if (value === undefined || value === null) {
        return ''
    }

    if (typeof value === 'string' || typeof value === 'number') {
        return String(value)
    }

    return ''
}

const getOptionTooltipText = (option, getOptionLabel) => {
    if (!option) {
        return ''
    }

    const optionLabel = getOptionLabel?.(option)
    const labelText = getDisplayText(optionLabel)

    if (labelText) {
        return labelText
    }

    return getDisplayText(option.label) || getDisplayText(option.value)
}

const flattenOptions = (options = []) =>
    options.flatMap((option) => (option.options ? option.options : option))

const getSelectTooltipTitle = ({
    value,
    defaultValue,
    placeholder,
    options,
    getOptionLabel,
    getOptionValue,
}) => {
    const currentValue = value !== undefined ? value : defaultValue
    const values = Array.isArray(currentValue)
        ? currentValue
        : currentValue
          ? [currentValue]
          : []

    const flattenedOptions = flattenOptions(options)
    const selectedText = values
        .map((option) => {
            if (typeof option === 'string' || typeof option === 'number') {
                const matchedOption = flattenedOptions.find((item) => {
                    const itemValue =
                        getOptionValue?.(item) ?? item?.value ?? item
                    return String(itemValue) === String(option)
                })

                return (
                    getOptionTooltipText(matchedOption, getOptionLabel) ||
                    String(option)
                )
            }

            return getOptionTooltipText(option, getOptionLabel)
        })
        .filter(Boolean)
        .join(', ')

    if (selectedText) {
        return selectedText
    }

    return placeholder || DEFAULT_PLACEHOLDER
}

const DefaultOption = ({
    innerProps,
    label,
    selectProps,
    isSelected,
    isDisabled,
    isFocused
}) => {
    const { themeColor } = selectProps
    return (
        <div
            className={
                classNames(
                    'select-option', 
                    isSelected && 'selected', 
                    isDisabled && 'disabled',
                    isFocused && 'focused'
                )
            }
            {...innerProps}
        >
            <span className="ml-2">{label}</span>
            {isSelected && (
                <HiCheck
                    className={`text-${themeColor} dark:text-white text-xl`}
                />
            )}
        </div>
    )
}

const DefaultDropdownIndicator = () => {
    return (
        <div className="select-dropdown-indicator">
            <HiChevronDown />
        </div>
    )
}

const DefaultClearIndicator = (props) => {
    const {
        innerProps: { ref, ...restInnerProps },
    } = props
    return (
        <div {...restInnerProps} ref={ref}>
            <div className="select-clear-indicator">
                <HiX />
            </div>
        </div>
    )
}

const DefaultLoadingIndicator = ({ selectProps }) => {
    const { themeColor } = selectProps
    return (
        <Spinner className={`select-loading-indicatior text-${themeColor}`} />
    )
}

const Select = React.forwardRef((props, ref) => {
    const {
        size,
        style,
        styles: customStyles,
        className,
        form,
        field,
        components,
        showTooltip,
        menuPortalTarget,
        menuPosition,
        onMenuOpen,
        onMenuClose,
        componentAs: Component,
        ...rest
    } = props
    const [menuOpen, setMenuOpen] = React.useState(false)

    const { themeColor, controlSize, primaryColorLevel, mode } = useConfig()
    const formControlSize = useForm()?.size
    const inputGroupSize = useInputGroup()?.size

    const selectSize = size || inputGroupSize || formControlSize || controlSize

    const twColor = theme`colors`
    const twHeight = theme`height`

    let isInvalid = false

    if (!isEmpty(form)) {
        const { touched, errors } = form

        const touchedField = get(touched, field.name)
        const errorField = get(errors, field.name)

        isInvalid = touchedField && errorField
    }

    const getBoxShadow = (state) => {
        const shadaowBase = '0 0 0 1px '

        if (isInvalid) {
            return shadaowBase + twColor.red['500']
        }

        if (state.isFocused) {
            return shadaowBase + twColor[themeColor][primaryColorLevel]
        }

        return 'none'
    }

    const styles = {
        container: (provided) => ({
            ...provided,
            width: '100%',
        }),
        control: (provided, state) => {
            return {
                ...provided,
                height: twHeight[CONTROL_SIZES[selectSize]],
                minHeight: twHeight[CONTROL_SIZES[selectSize]],
                '&:hover': {
                    boxShadow: getBoxShadow(state),
                    cursor: 'pointer',
                },
                boxShadow: getBoxShadow(state),
                borderRadius: tw`rounded-md`.borderRadius,
                ...(isInvalid ? { borderColor: twColor.red['500'] } : {}),
            }
        },
        input: (css) => {
            return {
                ...css,
                input: {
                    outline: 'none',
                    outlineOffset: 0,
                    boxShadow: 'none !important',
                },
            }
        },
        menu: (provided) => ({ ...provided, zIndex: SELECT_MENU_Z_INDEX }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: SELECT_MENU_Z_INDEX,
        }),
        ...style,
        ...customStyles,
    }

    const resolvedMenuPortalTarget =
        menuPortalTarget !== undefined
            ? menuPortalTarget
            : typeof document !== 'undefined'
              ? document.body
              : undefined
    const resolvedMenuPosition =
        menuPosition || (resolvedMenuPortalTarget ? 'fixed' : undefined)

    const selectClass = classNames('select', `select-${selectSize}`, className)
    const tooltipTitle = getSelectTooltipTitle({
        ...rest,
        getOptionLabel: rest.getOptionLabel,
        getOptionValue: rest.getOptionValue,
    })

    const handleMenuOpen = (...args) => {
        setMenuOpen(true)
        onMenuOpen?.(...args)
    }

    const handleMenuClose = (...args) => {
        setMenuOpen(false)
        onMenuClose?.(...args)
    }

    const selectNode = (
        <Component
            className={selectClass}
            classNamePrefix={'select'}
            ref={ref}
            styles={styles}
            theme={(theme) => ({
                ...theme,
                colors: {
                    ...theme.colors,
                    neutral20:
                        mode === 'dark'
                            ? twColor.gray['600']
                            : twColor.gray['300'],
                    neutral30:
                        mode === 'dark'
                            ? twColor.gray['600']
                            : twColor.gray['300'],
                    neutral80: twColor.gray['700'],
                    neutral10: 
                        mode === 'dark'
                            ? twColor.gray['600']
                            : twColor.gray['300'],
                    primary25: twColor[themeColor]['50'],
                    primary50: twColor[themeColor]['100'],
                    primary: twColor[themeColor][primaryColorLevel],
                },
            })}
            themeColor={`${themeColor}-${primaryColorLevel}`}
            components={{
                IndicatorSeparator: () => null,
                Option: DefaultOption,
                LoadingIndicator: DefaultLoadingIndicator,
                DropdownIndicator: DefaultDropdownIndicator,
                ClearIndicator: DefaultClearIndicator,
                ...components,
            }}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            menuPortalTarget={resolvedMenuPortalTarget}
            menuPosition={resolvedMenuPosition}
            {...field}
            {...rest}
        />
    )

    if (!showTooltip) {
        return selectNode
    }

    return (
        <Tooltip
            disabled={menuOpen}
            title={tooltipTitle}
            wrapperClass="select-tooltip-wrapper"
        >
            {selectNode}
        </Tooltip>
    )
})

Select.propTypes = {
    size: PropTypes.oneOf([SIZES.LG, SIZES.MD, SIZES.SM]),
    componentAs: PropTypes.elementType,
    showTooltip: PropTypes.bool,
    style: PropTypes.object,
    styles: PropTypes.object,
    menuPortalTarget: PropTypes.any,
    menuPosition: PropTypes.oneOf(['absolute', 'fixed']),
}

Select.defaultProps = {
    componentAs: ReactSelect,
    showTooltip: true,
}

export default Select
